import { 
  collection, 
  doc, 
  getDoc, 
  setDoc, 
  updateDoc,
  serverTimestamp 
} from 'firebase/firestore';
import { db } from '../config/firebase';
import type { SEOSettings } from '../types';

const SEO_COLLECTION = 'seoSettings';
const SEO_DOC_ID = 'main';

class SEOService {
  // SEO ayarlarını getir
  async getSEOSettings(): Promise<SEOSettings | null> {
    try {
      const docRef = doc(db, SEO_COLLECTION, SEO_DOC_ID);
      const docSnap = await getDoc(docRef);
      
      if (docSnap.exists()) {
        return { id: docSnap.id, ...docSnap.data() } as SEOSettings;
      }
      
      return null;
    } catch (error) {
      console.error('Error fetching SEO settings:', error);
      throw error;
    }
  }

  // SEO ayarlarını güncelle
  async updateSEOSettings(settings: Partial<SEOSettings>): Promise<void> {
    try {
      const docRef = doc(db, SEO_COLLECTION, SEO_DOC_ID);
      const docSnap = await getDoc(docRef);
      
      const updateData = {
        ...settings,
        updatedAt: serverTimestamp()
      };
      
      if (docSnap.exists()) {
        await updateDoc(docRef, updateData);
      } else {
        await setDoc(docRef, {
          ...updateData,
          createdAt: serverTimestamp()
        });
      }
    } catch (error) {
      console.error('Error updating SEO settings:', error);
      throw error;
    }
  }

  // Varsayılan SEO ayarlarını oluştur
  async createDefaultSEOSettings(): Promise<void> {
    try {
      const defaultSettings: Omit<SEOSettings, 'id' | 'createdAt' | 'updatedAt'> = {
        siteName: 'HatTravel',
        siteDescription: 'Hatay\'dan dünyaya açılan kapınız. Unutulmaz seyahat deneyimleri için HatTravel ile keşfe çıkın.',
        siteKeywords: ['hatay', 'seyahat', 'tur', 'tatil', 'gezi', 'turizm', 'hattravel'],
        siteUrl: 'https://hattravel.com',
        defaultLanguage: 'tr',
        
        defaultTitle: 'HatTravel - Hatay\'dan Dünyaya Seyahat',
        titleTemplate: '%s | HatTravel',
        defaultMetaDescription: 'Hatay\'dan dünyaya açılan kapınız. Unutulmaz seyahat deneyimleri için HatTravel ile keşfe çıkın.',
        defaultMetaKeywords: ['hatay', 'seyahat', 'tur', 'tatil', 'gezi', 'turizm'],
        
        openGraph: {
          siteName: 'HatTravel',
          type: 'website',
          defaultImage: 'https://hattravel.com/images/og-image.jpg',
          locale: 'tr_TR'
        },
        
        twitterCard: {
          cardType: 'summary_large_image',
          site: '@hattravel',
          creator: '@hattravel',
          defaultImage: 'https://hattravel.com/images/twitter-image.jpg'
        },
        
        schema: {
          enableOrganizationSchema: false,
          organizationName: 'HatTravel',
          organizationLogo: 'https://hattravel.com/images/logo.png',
          organizationPhone: '+90 555 123 4567',
          organizationEmail: 'info@hattravel.com',
          organizationAddress: 'Antakya, Hatay, Türkiye'
        },
        
        technical: {
          enableRobotsTxt: true,
          enableSitemap: true,
          enableCanonical: true,
          robotsTxtContent: `User-agent: *
Allow: /
Disallow: /admin/
Disallow: /api/
Sitemap: https://hattravel.com/sitemap.xml`
        },
        
        analytics: {
          googleAnalyticsId: '',
          googleTagManagerId: '',
          facebookPixelId: '',
          customAnalyticsCode: ''
        },
        
        hreflangTags: [],
        
        pageSettings: {
          home: {
            title: 'HatTravel - Hatay\'dan Dünyaya Seyahat',
            description: 'Hatay\'dan dünyaya açılan kapınız. Unutulmaz seyahat deneyimleri için HatTravel ile keşfe çıkın.',
            keywords: ['hatay', 'seyahat', 'tur', 'tatil', 'gezi', 'turizm', 'antakya']
          },
          tours: {
            title: 'Tur Paketleri - HatTravel',
            description: 'En iyi fiyatlarla kaliteli tur paketleri. Hatay\'dan dünyaya seyahat edin.',
            keywords: ['tur paketleri', 'seyahat turları', 'tatil paketleri', 'gezi turları']
          },
          blog: {
            title: 'Seyahat Rehberi - HatTravel Blog',
            description: 'Seyahat ipuçları, gezi rehberleri ve destinasyon önerileri.',
            keywords: ['seyahat rehberi', 'gezi ipuçları', 'destinasyon rehberi', 'seyahat blogu']
          },
          about: {
            title: 'Hakkımızda - HatTravel',
            description: 'HatTravel olarak misyonumuz, vizyonumuz ve değerlerimiz hakkında bilgi edinin.',
            keywords: ['hattravel hakkında', 'seyahat acentesi', 'turizm şirketi']
          },
          contact: {
            title: 'İletişim - HatTravel',
            description: 'HatTravel ile iletişime geçin. Sorularınızı yanıtlayalım.',
            keywords: ['iletişim', 'telefon', 'adres', 'email', 'destek']
          }
        }
      };
      
      await this.updateSEOSettings(defaultSettings);
    } catch (error) {
      console.error('Error creating default SEO settings:', error);
      throw error;
    }
  }
}

export const seoService = new SEOService(); 